import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { getCalendarEventsMenuContentList } from "@/domains/calendar/getCalendarEventsMenuContentList";
import { EventContext } from "@/domains/metrics/context";
import {
  MDSActionMenuDropdown,
  MDSMenuDropdownSlotName,
  MDSIconButton,
  MDSIconKind,
  MDSIconButtonSize,
  MDSMenuContentItemKind,
  MDSMenuContentList,
} from "@/mds";
import { useAppStore } from "@/store";
import { SettingsTabId } from "@/store/pages/SettingsPageStore";
import { useMemo, useState } from "react";
import { NotesListPageConnectCalendarLaterModal } from "@/pages/notes/components/notes-list-page-connect-calendar-later-modal";
import { NotesListPageCalendarSyncStatus } from "@/pages/notes/components/notes-list-page-calendar-sync-status";

export const NotesListPageListUpcomingEventsHeaderButtons = observer(() => {
  const { store } = useAppStore();

  const [isConnectCalendarLaterModalOpen, setIsConnectCalendarLaterModalOpen] = useState(false);
  const hasCalendarConnections = store.spaceAccountCalendarConnections.size > 0;
  const calendarEvents = store.spaceAccountCalendarEvents.ongoingAndUpcomingEvents;
  const calendarDropdownContentList: MDSMenuContentList = getCalendarEventsMenuContentList({
    calendarEvents,
    createNoteUsingCalendarEventId: ({ noteId, calendarEventId }) =>
      store.notes.createNoteUsingCalendarEventId({ noteId, calendarEventId }),
    navigateToNote: ({ noteId, openInSameTab }: { noteId: string; openInSameTab: boolean }) =>
      store.navigation.goToNote({ noteId, openInSameTab }),
    store,
    context: EventContext.NotesCalendarEventDropdown,
  });

  const moreDropdownContentList: MDSMenuContentList = useMemo(
    () =>
      hasCalendarConnections
        ? [
            {
              id: "calendar-settings",
              kind: MDSMenuContentItemKind.Item,
              label: "Calendar settings",
              icon: MDSIconKind.Settings,
              onClick: () => store.settingsModal.open(SettingsTabId.CalendarAndMeetingsPage),
            },
          ]
        : [
            {
              id: "hide-calendar-events",
              kind: MDSMenuContentItemKind.Item,
              label: "Hide this section",
              icon: MDSIconKind.EyeSlash,
              onClick: () => {
                setIsConnectCalendarLaterModalOpen(true);
              },
            },
          ],
    [hasCalendarConnections, store.settingsModal]
  );

  return (
    <>
      {isConnectCalendarLaterModalOpen && (
        <NotesListPageConnectCalendarLaterModal
          isOpen={isConnectCalendarLaterModalOpen}
          handleClose={() => setIsConnectCalendarLaterModalOpen(false)}
        />
      )}
      <StyledSyncStatusWrapper>
        <NotesListPageCalendarSyncStatus />
      </StyledSyncStatusWrapper>
      {hasCalendarConnections && calendarDropdownContentList.length > 0 && (
        <MDSActionMenuDropdown contentList={calendarDropdownContentList} placement="bottom right">
          <MDSIconButton
            slot={MDSMenuDropdownSlotName.Trigger}
            iconKind={MDSIconKind.Calendar}
            size={MDSIconButtonSize.Small}
          />
        </MDSActionMenuDropdown>
      )}
      <MDSActionMenuDropdown contentList={moreDropdownContentList} placement="bottom right">
        <MDSIconButton
          slot={MDSMenuDropdownSlotName.Trigger}
          iconKind={MDSIconKind.More}
          size={MDSIconButtonSize.Small}
        />
      </MDSActionMenuDropdown>
    </>
  );
});

const StyledSyncStatusWrapper = styled.div(({ theme }) => ({
  marginRight: theme.spacing[2],
}));
